// Lightweight User Timing helpers.
// These create entries in the Performance panel "Timings" track
// with very little runtime overhead. Console logging is opt-in.

const hasPerf = typeof performance !== 'undefined' && !!performance.mark
const shouldConsole = (() => {
  if (typeof window !== 'undefined' && (window as any).__PERF_LOG__) return true
  if (typeof localStorage !== 'undefined' && localStorage.getItem('debug:perf') === '1') return true
  return false
})()

// Clear measures immediately by default: several are recorded per pointer move, and
// retained User Timing entries grow without bound over a long session.
// Set localStorage 'perf:keep' = '1' to keep them visible in Performance Timings.
const keepMeasures = (() => {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('perf:keep') === '1'
    }
  } catch { }
  return false
})()

// Sequence counters for unique measures per base name
const seq = new Map<string, number>()
function nextSeq(name: string) {
  const n = (seq.get(name) || 0) + 1
  seq.set(name, n)
  return n
}

export function mark(name: string) {
  if (!hasPerf) return
  try { performance.mark(name) } catch { }
}

export function start(name: string) {
  if (!hasPerf) return
  try { performance.mark(`${name}:start`) } catch { }
}

export function end(name: string) {
  if (!hasPerf) return
  try {
    const s = `${name}:start`
    const e = `${name}:end`
    performance.mark(e)
    performance.measure(name, s, e)
    // Clean to avoid unbounded memory during long sessions
    performance.clearMarks(s)
    performance.clearMarks(e)
  } catch { }
}

export function measure(name: string, startMark: string, endMark: string) {
  if (!hasPerf) return
  try { performance.measure(name, startMark, endMark) } catch { }
}

export function timeFunction<T>(label: string, fn: () => T): T {
  if (!hasPerf) return fn()
  const s = `${label}:start`
  const e = `${label}:end`
  try { performance.mark(s) } catch { }
  const result = fn()
  try {
    performance.mark(e)
    performance.measure(label, s, e)
    if (shouldConsole) {
      const m = performance.getEntriesByName(label).slice(-1)[0]
      if (m) console.log(`${label}: ${m.duration.toFixed(2)}ms`)
    }
    performance.clearMarks(s)
    performance.clearMarks(e)
    if (!keepMeasures) performance.clearMeasures(label)
  } catch { }
  return result
}

export async function timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
  if (!hasPerf) return fn()
  const s = `${label}:start`
  const e = `${label}:end`
  try { performance.mark(s) } catch { }
  const result = await fn()
  try {
    performance.mark(e)
    performance.measure(label, s, e)
    if (shouldConsole) {
      const m = performance.getEntriesByName(label).slice(-1)[0]
      if (m) console.log(`${label}: ${m.duration.toFixed(2)}ms`)
    }
    performance.clearMarks(s)
    performance.clearMarks(e)
    if (!keepMeasures) performance.clearMeasures(label)
  } catch { }
  return result
}

// Unique-named measure for repeated spans (helps DevTools show all instances).
export function timeFunctionMulti<T>(label: string, fn: () => T): T {
  if (!hasPerf) return fn()
  const n = nextSeq(label)
  const base = `${label} #${n}`
  const s = `${base}:start`
  const e = `${base}:end`
  try { performance.mark(s) } catch { }
  const result = fn()
  try {
    performance.mark(e)
    performance.measure(base, s, e)
    if (shouldConsole) {
      const m = performance.getEntriesByName(base).slice(-1)[0]
      if (m) console.log(`${base}: ${m.duration.toFixed(2)}ms`)
    }
    performance.clearMarks(s)
    performance.clearMarks(e)
    if (!keepMeasures) performance.clearMeasures(base)
  } catch { }
  return result
}

// Start/end an explicitly controlled unique span
export function startSpan(label: string): string {
  if (!hasPerf) return ''
  const n = nextSeq(label)
  const base = `${label} #${n}`
  try { performance.mark(`${base}:start`) } catch { }
  return base
}

export function endSpan(base: string) {
  if (!hasPerf || !base) return
  try {
    const s = `${base}:start`
    const e = `${base}:end`
    performance.mark(e)
    performance.measure(base, s, e)
    performance.clearMarks(s)
    performance.clearMarks(e)
    if (!keepMeasures) performance.clearMeasures(base)
  } catch { }
}

// Record a React commit as a measure without creating extra marks.
// Uses the modern Performance.measure options if available.
export function measureReactCommit(name: string, startTime: number, commitTime: number) {
  if (!hasPerf) return
  const duration = Math.max(0, commitTime - startTime)
  try {
    // Use a unique name so DevTools renders each commit separately
    const base = `${name} #${nextSeq(name)}`
      ; (performance as any).measure(base, { start: startTime, duration })
  } catch {
    // Fallback to a simple measurement at commit time
    try {
      const base = `${name} #${nextSeq(name)}`
      performance.mark(`${base}:start`)
      performance.mark(`${base}:end`)
      performance.measure(base, `${base}:start`, `${base}:end`)
      performance.clearMarks(`${base}:start`)
      performance.clearMarks(`${base}:end`)
      if (!keepMeasures) performance.clearMeasures(base)
    } catch { }
  }
}
